import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import { PaxDataDef, useCardReader } from 'Src/feature/card-reader/useCardReader';
import { useCashDrawer } from 'Src/feature/cash-drawer/useCashDrawer';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useCurrentEmployee } from 'Src/feature/employee/useCurrentEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { UsedModalSearchParamControl, useModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { CardReaderRequest, CardReaderType } from 'Src/feature/payment/paymentService';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useToast } from 'Src/feature/toasts/useToast';
import { sortByLabel } from 'Src/utils/sort';

import { PayInOutTxPayload, PayInPaymentMethod } from './payInOutService';
import { usePayInOut } from './usePayInOut';

const { Form, Field } = getTypedFormComponents<PayInFormModel>();

interface PayInFormModel extends PayInOutTxPayload {
  paymentMethod: PayInPaymentMethod;
  ccPaymentMethodId?: number;
  cashPaymentMethodId?: number;
}

const sectionId = 'usePayIn';

const usePayIn = (baseModalControl: UsedModalSearchParamControl) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast({ persist: true });
  const cardReader = useCardReader();

  const payInOuts = usePayInOut.query.getAll();
  const payIn = usePayInOut.mutate.payIn();
  const payInReasons = usePayInOut.query.reason.options.payIn();
  const ccPaymentMethod = usePaymentMethod.query.ccMethods();
  const cashPaymentMethod = usePaymentMethod.query.cashMethods();
  const cashDrawer = useCashDrawer();
  const currentOperator = useCurrentOperator();
  const employee = useCurrentEmployee();
  const terminal = useTerminal.query.get();

  const [statusFailMessage, statusFailMessageSet] = useState(undefined as undefined | string);

  const form = useForm<PayInFormModel>({
    defaultValues: {
      paymentMethod: PayInPaymentMethod.Cash,
    },
    schema: object().shape({
      amount: number().label(t('payInOut.labels.payInAmount')).moreThan(0).required(),
      payInOutReasonId: number().label(t('labels.whatIsItFor')).required(),
    }),
  });

  const confirmModal = useModalSearchParamControl({ value: 'pay-in-confirm' });
  const doneModal = useModalSearchParamControl({
    value: 'pay-in-done',
    onClose: () => {
      statusFailMessageSet(undefined);
    }
  });
  const cardModal = useModalSearchParamControl({
    value: 'pay-in-card',
    onOpen: () => {
      cardReader.init({
        mode: 'emv',
        amount: form.action.getValues().amount,
        onSuccess: async (read) => {
          if (!ccPaymentMethod.data || ccPaymentMethod.data.length === 0) {
            log.warn('cardReader.onSuccess()', 'Transaction dependency missing', { ['ccPaymentMethod']: ccPaymentMethod.data });
            return;
          }

          if (!read || !read?.pax) {
            // TODO: when would this happen
            log.error('cardReader.onSuccess()', 'no pax data received');
            return;
          }

          const ccPayMethodCount = cardReaderCCMethodOptions.length;
          if (ccPayMethodCount < 1) {
            log.debug('cardReader.onSuccess()', 'No cc payment methods set up to work with card reader...');
            return;
          }

          const { isValid, values } = await form.action.submit();

          if (!isValid) {
            log.debug('cardReader.onSuccess()', 'form is invalid');
            read.reInit();
            return;
          }

          try {
            if (typeof employee?.id !== 'number') return log.debug('cardReader.onSuccess()', 'payIn', '!employee.id');
            if (typeof terminal.data?.terminal.locationId !== 'number') return log.debug('cardReader.onSuccess()', 'payIn', '!location.id');
            if (typeof terminal.data?.terminal.id !== 'number') return log.debug('cardReader.onSuccess()', 'payIn', '!terminal.id');
            const response = await payIn.mutateAsync({
              payInOutTransaction: {
                amount: values.amount,
                notes: values.notes,
                payInOutReasonId: values.payInOutReasonId,
                paymentMethodId: values.ccPaymentMethodId ?? cardReaderCCMethodOptions[0]?.value,
                isVoid: false,
                locationId: terminal.data?.terminal.locationId,
                posTerminalId: terminal.data?.terminal.id,
              },
              transaction: {
                cardReaderType: CardReaderType.pax,
                paymentTypeId: values.ccPaymentMethodId ?? cardReaderCCMethodOptions[0]?.value,
                paxData: read.pax as unknown as PaxDataDef,
                total: values.amount,
                isOpenTab: false,
              },
            });
            log.info('cardReader.onSuccess()', 'payIn', { response });
            if (response.status === 'Success' || response.status === 'Pending') {
              cardModal.close();
              doneModal.open();
              await payInOuts.refetch();
            } else {
              cardModal.close();
              toast.show.error({ id: `${sectionId}-pay-in-declined`, message: t('payment.info.cardDeclined') });
            }
          } catch (err) {
            log.error('cardReader.onSuccess()', 'payIn', err);
            toast.show.serviceError(err, { id: `${sectionId}-pay-in` });
            /*
             * Reinitialize card reader in the case of an error ie. API 500
             * In the case of a non sucessfull response `Success` | `Pending` the modal is closed,
             * the card reader should be reinitialize when the modal is opened again.
             */
            read.reInit();
          }
        },
        // when true, reinit card read w last init option
        onTimeout: () => true,
        // when true, reinit card read w last init option
        onError: () => true,
      });
    },
    onClose: () => {
      void cardReader.dispose();
    }
  });

  const allowedPaymentMethodIds = currentOperator.permission('pos.money.payIn.execute')?.metadata?.paymentMethodIds;

  const cardReaderCCMethodOptions = useMemo(() => ccPaymentMethod.data?.filter((pm) => allowedPaymentMethodIds?.includes(pm.id as number))
    .map((pm) => ({ value: pm.id as number, label: pm.name }))
    .sort(sortByLabel) ?? [], [allowedPaymentMethodIds, ccPaymentMethod.data]);

  const cashPaymentMethodOptions = useMemo(() => cashPaymentMethod.data?.filter((pm) => allowedPaymentMethodIds?.includes(pm.id as number))
    .map((pm) => ({ value: pm.id as number, label: pm.name }))
    .sort(sortByLabel) ?? [], [allowedPaymentMethodIds, cashPaymentMethod.data]);

  const formValues = form.state.watch();

  const is = {
    manualCC: formValues.paymentMethod === PayInPaymentMethod.ManualCardEntry,
    cash: formValues.paymentMethod === PayInPaymentMethod.Cash,
    cardReader: formValues.paymentMethod === PayInPaymentMethod.CardReader,
  };

  return {
    modal: {
      base: baseModalControl,
      confirm: confirmModal,
      done: doneModal,
      card: cardModal,
    },
    form,
    formValues,
    canSubmit: form.state.canTrySubmit,
    options: {
      cardReaderCCMethods: cardReaderCCMethodOptions,
      otherPaymentMethods: cashPaymentMethodOptions,
      payInOutPaymentMethods: [
        { value: PayInPaymentMethod.Cash, label: t('labels.cash') },
        { value: PayInPaymentMethod.CardReader, label: t('cardReader.label') },
        { value: PayInPaymentMethod.ManualCardEntry, label: t('labels.manualCC') },
      ],
      payInReasons: payInReasons.data,
    },
    connectedCardReader: cardReader.state.connectedReader,
    employeeFullName: employee ? t('labels.util.employeeFullName', employee) : '',
    // "statusMessage"
    // - default to success message
    // - should switch success/fail from PayInManual
    statusMessage: statusFailMessage || t('payInOut.labels.payInConfirmSuccess'),
    statusFailMessageSet,
    is,
    onClose: () => {
      baseModalControl.close();
      confirmModal.close();
      doneModal.close();
      cardModal.close();
      form.action.reset();
    },
    onSave: useCallback(async () => {
      await form.action.submitIfValid(async ({ values }) => {
        if (is.cash) {
          try {
            if (typeof employee?.id !== 'number') return log.debug('cardReader.onSuccess()', 'payIn', '!employee.id');
            if (typeof terminal.data?.terminal.locationId !== 'number') return log.debug('cardReader.onSuccess()', 'payIn', '!location.id');
            if (typeof terminal.data?.terminal.id !== 'number') return log.debug('cardReader.onSuccess()', 'payIn', '!terminal.id');
            const response = await payIn.mutateAsync({
              payInOutTransaction: {
                amount: values.amount,
                notes: values.notes,
                payInOutReasonId: values.payInOutReasonId,
                paymentMethodId: values.cashPaymentMethodId ?? cashPaymentMethodOptions[0]?.value,
                isVoid: false,
                locationId: terminal.data?.terminal.locationId,
                posTerminalId: terminal.data?.terminal.id,
              },
              transaction: {
                cardReaderType: 'none' as CardReaderRequest['cardReaderType'],
                isOpenTab: false,
                paymentTypeId: values.cashPaymentMethodId ?? cashPaymentMethodOptions[0]?.value,
              },
            });
            log.debug('onSave()', 'is.cash', { response });
            if (response) {
              cashDrawer.popDrawer();
              confirmModal.close();
              doneModal.open();
              await payInOuts.refetch();
            }
          } catch (err) {
            toast.show.serviceError(err, { id: `S{sectionId}.payInCash` });
          }
        }
      });
    }, [
      cashDrawer,
      cashPaymentMethodOptions,
      confirmModal,
      doneModal,
      employee?.id,
      form.action,
      is.cash,
      log,
      payIn,
      payInOuts,
      terminal.data?.terminal.id,
      terminal.data?.terminal.locationId,
      toast.show,
    ]),
    onRecordPayIn: async () => {
      await form.action.submitIfValid(async () => {
        if (is.cash) {
          confirmModal.open();
        } else if (is.cardReader) {
          cardModal.open();
        }
      });
    },
  };
};

type UsedPayIn = ReturnType<typeof usePayIn>;

export { Field, Form, usePayIn };
export type { UsedPayIn };
